import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Trash2, Loader2, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";
import KidsFeedback, { OUTCOMES } from "../components/KidsFeedback";

export default function LittleWins() {
  const { activeId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [food, setFood] = useState("");
  const [suggest, setSuggest] = useState({});
  const [loadingSug, setLoadingSug] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data } = await api.get(`/wins/${activeId}`);
    setData(data);
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const getSuggestions = async (memberId) => {
    setLoadingSug(memberId);
    try { const { data } = await api.get(`/wins/${activeId}/suggest/${memberId}`); setSuggest((s) => ({ ...s, [memberId]: data })); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoadingSug(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/wins/${id}`); load(); } catch { toast.error("Couldn't remove that"); }
  };

  const kids = data?.children?.map((c) => c.member) || [];

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#E7F0EA] pb-28">
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate("/millie")} data-testid="wins-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-4xl text-sorted-ink">Little Wins</h1>
          <span className="w-11" />
        </div>

        <div className="px-6 mt-4 flex items-end gap-2">
          <MillieHero mood="celebrate" className="h-32 w-28 shrink-0" testId="wins-millie" />
          <SpeechBubble testId="wins-millie-bubble" className="flex-1">No pressure, no clean-plate club. We just notice the little wins and build from what they already love. ❤️</SpeechBubble>
        </div>

        {!data ? (
          <div className="px-6 mt-6"><div className="h-40 rounded-3xl bg-white/60 animate-pulse" /></div>
        ) : kids.length === 0 ? (
          <p className="px-6 mt-6 text-sm text-sorted-slate" data-testid="wins-no-kids">Add a child to your household and I'll start tracking their little wins.</p>
        ) : (
          <>
            <div className="px-6 mt-6 bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft mx-6" data-testid="wins-log">
              <p className="font-heading font-semibold text-sorted-ink">Log a food</p>
              <p className="text-xs text-sorted-slate mb-2">What did they have a go at?</p>
              <input data-testid="wins-food-input" value={food} onChange={(e) => setFood(e.target.value)} placeholder="e.g. Broccoli, salmon, avocado…"
                className="w-full bg-muted rounded-2xl px-4 py-3 font-medium text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach" />
              {food.trim() && <div className="mt-3"><KidsFeedback kids={kids} activeId={activeId} food={food.trim()} onLogged={() => { setFood(""); load(); }} /></div>}
            </div>

            <div className="px-6 mt-6 space-y-5">
              {data.children.map(({ member, wins, stats }) => (
                <div key={member.id} className="bg-white rounded-3xl border-2 border-[#FDECEF] shadow-soft p-4" data-testid={`wins-child-${member.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-heading text-lg font-semibold text-sorted-ink">{member.name}</h2>
                      <p className="text-xs text-sorted-slate font-semibold">{member.age != null ? `${member.age} yrs · ` : ""}fussy {member.fussy_level}/5</p>
                    </div>
                    <span className="flex items-center gap-1 bg-sorted-sage text-sorted-ink text-xs font-bold px-3 py-1.5 rounded-full"><Star size={13} strokeWidth={2.5} />{stats.liked + stats.tried} wins</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {OUTCOMES.map((o) => (
                      <div key={o.key} className={`${o.cls} rounded-2xl py-2 text-center`}>
                        <p className="font-heading font-bold text-lg text-sorted-ink leading-none">{stats[o.key]}</p>
                        <p className="text-[10px] font-bold text-sorted-ink/70">{o.label}</p>
                      </div>
                    ))}
                  </div>

                  {wins.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {wins.slice(0, 6).map((w) => {
                        const o = OUTCOMES.find((x) => x.key === w.outcome);
                        return (
                          <div key={w.id} className="flex items-center gap-2 text-sm" data-testid={`win-${w.id}`}>
                            <span className={`w-2 h-2 rounded-full ${o?.cls}`} />
                            <span className="flex-1 font-semibold text-sorted-ink truncate">{w.food}</span>
                            <span className="text-xs text-sorted-slate">{o?.label}</span>
                            <button onClick={() => remove(w.id)} aria-label="Remove" className="text-sorted-slate"><Trash2 size={13} strokeWidth={2.5} /></button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {suggest[member.id] ? (
                    <div className="mt-4 bg-[#FFF6F1] rounded-2xl p-3" data-testid={`wins-suggest-${member.id}`}>
                      <p className="font-hand text-lg text-sorted-ink">{suggest[member.id].encouragement}</p>
                      <div className="mt-2 space-y-2">
                        {suggest[member.id].suggestions.map((s, i) => (
                          <div key={i} className="text-sm">
                            <p className="font-bold text-sorted-ink">{s.food}</p>
                            <p className="text-xs text-sorted-slate">{s.why} — {s.how}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => getSuggestions(member.id)} disabled={loadingSug === member.id} data-testid={`wins-suggest-btn-${member.id}`}
                      className="mt-4 w-full bg-sorted-rose text-white text-sm font-bold py-3 rounded-full flex items-center justify-center gap-2">
                      {loadingSug === member.id ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={2.5} />} Gentle next tries
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
